import classNames from 'classnames';
import { Button, Label, Radio } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select, { MultiValue, SingleValue } from 'react-select';
import {
	camelCaseToSpace,
	designationMappingWithTechStack,
	Dropdown,
	formatCareerPathType,
	techStackMappingWithSkills,
} from 'shared-utils';
import { PrettoSlider } from 'src/components/university/UniversityStudentFilters';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetAllDrivesQuery } from 'src/hooks/drives.hooks';
import {
	CompanyDocument,
	SkillType,
	TechStacksForPosition,
	UIPosition,
	UserCareerPathType,
	UserExperienceLevelType,
	WorkingMode,
} from 'types';
import { locationPreferenceOptionsData } from '../campus/campusCandidateListing/LocationFilterInput';
import { useGetAllSkills } from 'src/hooks/skill.hooks';
import RecruiterPositionFormHeader from './RecruiterPositionFormHeader';
import { salaryOptions } from '../campus/campusCandidateListing/SalaryFilterInput';
import { useMutation, useQueryClient } from 'react-query';
import { createPositionV2 } from 'src/apis/position';
import { get } from 'lodash';
import { educationOptionsData } from '../campus/campusCandidateListing/EducationFilterInput';
import { availabilityOptions } from '../campus/campusCandidateListing/AvailabilityFilterInput';
import { useLayoutContext } from 'src/context/LayoutProvider';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';

type FormDataType = {
	careerPath: UserCareerPathType;
	techStacks?: string[];
	location: string[];
	salary: { min: number; max: number };
	experience?: { min: number; max: number };
	workingMode: WorkingMode;
	company?: CompanyDocument;
	degrees?: string[];
	expectedJoining?: string;
};

export const findSkillsFromTechStacks = (techStacks: TechStacksForPosition[]): string[] => {
	return techStacks.reduce((result: string[], techStack) => {
		const skills = techStackMappingWithSkills[techStack] || [];
		// Append skills of the current tech stack to the result array, ensuring no duplicates
		skills.forEach(skill => {
			if (!result.includes(skill)) {
				result.push(skill);
			}
		});
		return result;
	}, []);
};

export const findTechStacksFromSkills = (selectedSkills: string[]): TechStacksForPosition[] => {
	return Object.entries(techStackMappingWithSkills).reduce((result: TechStacksForPosition[], [techStack, skills]) => {
		if (selectedSkills.some(skill => skills.includes(skill))) {
			result.push(techStack as TechStacksForPosition);
		}
		return result;
	}, []);
};

// Function to find the position title from the tech stacks
export const positionTitleFinder = (selectedTechStacks: TechStacksForPosition[]) => {
	return (
		Object.entries(designationMappingWithTechStack).find(([positionTitle, techStacks]) => {
			return selectedTechStacks.every(stack => techStacks.includes(stack));
		})?.[0] || null
	);
};

const PositionCreationModalFormForRecruiter = ({
	onFinish,
	isModal = false,
	loading = false,
	experienceLevelBuckets,
}: {
	isModal?: boolean;
	loading?: boolean;
	onFinish?: (data: { formData: FormDataType; position?: UIPosition }) => void;
	experienceLevelBuckets: UserExperienceLevelType[];
}) => {
	const toast = useToast();
	const navigate = useNavigate();
	const { user } = useUserContext();
	const queryClient = useQueryClient();
	const { setShowLoadingAnimation } = useLayoutContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const drives: string[] = JSON.parse(searchParams.get('drive') || '[]');
	const { data, isLoading, refetch } = useGetAllDrivesQuery();
	const drive = data?.drives.find(item => drives?.includes(item?._id) && item?.isRegistered === false);
	const [techStacks, setTechStacks] = useState<Dropdown[]>([]);
	const [positionTitle, setPositionTitle] = useState<Dropdown[]>([]);
	const [isSticky, setIsSticky] = useState(false);
	const selectedRole = JSON.parse(searchParams.get('role') || '[]');
	const selectedSkills = JSON.parse(searchParams.get('skills') || '[]');
	const salary = JSON.parse(searchParams.get('salary') || '[]');
	const degree = JSON.parse(searchParams.get('education') || '[]');
	const expectedJoining = JSON.parse(searchParams.get('availability') || '[]');
	const locations = JSON.parse(searchParams.get('location') || '[]');
	const availabilityOptionsWithAny = [{ value: 'Any', label: 'Any' }, ...availabilityOptions];
	const educationOptionsDataWithAny = [{ value: 'Any', label: 'Any' }, ...educationOptionsData];
	const locationOptionsDataWithAny = [{ value: 'Any', label: 'Any' }, ...locationPreferenceOptionsData];

	const salaryData = salaryOptions?.find(e => salary?.includes(e?.value))?.data;
	const degreeData = educationOptionsData?.filter(e => degree?.includes(e?.value))?.map(e => e?.value);
	const expectedJoiningData = expectedJoining?.length
		? availabilityOptionsWithAny?.find(e => e?.value === expectedJoining[0])?.value
		: '';

	const _limits = { top: 0, bottom: 130 }; // Define your limits here
	const STATES = { Static: 'static', Fixed: 'fixed', Stuck: 'stuck' }; // Define your states here

	const { data: skillsData } = useGetAllSkills({
		type: SkillType.SKILL,
	});

	const skillOptions = skillsData?.skills
		?.filter(skill => skill?.skillGroupType)
		?.map(skill => ({ value: skill?._id, label: skill?.name?.replaceAll('_', ' ') }));

	const selectedSkillsData = skillsData?.skills?.filter(skill => selectedSkills?.includes(skill?._id));
	const { data: positionTitleData } = useGetDropdownItemsByCategory('POSITION_TITLE', undefined, true);
	const roleHiringOptions: Dropdown[] =
		positionTitleData?.lmsTechStacks?.map(item => ({
			label: item.label || item.name,
			value: item.name,
		})) || [];

	const _calcState = (windowOffset: number) => {
		if (windowOffset < _limits.top) {
			return STATES.Static; // Return Static if above the top limit
		} else if (windowOffset >= _limits.bottom) {
			return STATES.Stuck; // Return Stuck if below the bottom limit
		}
		return STATES.Fixed; // Return Fixed if within limits
	};

	useEffect(() => {
		const handleScroll = () => {
			const windowOffset = window.scrollY; // Get the current scroll position
			const state = _calcState(windowOffset); // Call _calcState with the current scroll position
			setIsSticky(state === STATES.Stuck); // Update isSticky based on the state
		};

		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const [formData, setFormData] = useState<FormDataType>({
		careerPath: selectedRole?.length ? selectedRole[0] : UserCareerPathType.SOFTWARE_DEVELOPMENT,
		techStacks: selectedSkillsData?.length ? selectedSkills : undefined,
		location: locations?.length ? locations : ['Any'],
		workingMode: WorkingMode.FULL_TIME,
		salary: salaryData || { min: 1, max: 80 },
		degrees: degreeData?.length ? degreeData : ['Any'],
		expectedJoining: expectedJoiningData || 'Any',
		company: user?.registeredCompany,
	});

	const createPositionV2Fn = useMutation(['createPositionV2'], createPositionV2, {
		onSuccess(data) {
			queryClient.refetchQueries({
				queryKey: ['getMyRegisteredPositions'],
				exact: false, // This allows refetching of all matching queries
			});
			toast({
				title: 'Position created successfully',
				type: 'success',
			});

			if (onFinish) {
				onFinish({ formData, position: data?.position });
			} else {
				setShowLoadingAnimation(false);
				navigate(`/view-candidates/position/${data?.position?._id}`);
			}
		},
		onError(error: Error) {
			setShowLoadingAnimation(false);
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error while creating position: ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		setShowLoadingAnimation(true);
		createPositionV2Fn.mutate({
			mustHaveSkills: formData?.techStacks || [],
			salary: formData?.salary,
			careerPath: formData?.careerPath,
			workExperience: formData?.experience || { min: 0, max: 15 },
			workLocations: formData?.location || [],
			workingMode: formData?.workingMode,
			company: user?.registeredCompany as CompanyDocument,
			positionExperienceBuckets: experienceLevelBuckets || [UserExperienceLevelType.FRESHER],
			degrees: formData?.degrees,
			expectedJoining: (formData?.expectedJoining || 'Immediate') as any,
			workLocation: ''
		});
	};

	return (
		<div className={classNames('mx-auto [&_input]:bg-white w-full', isModal ? '' : 'max-w-[760px]')}>
			<div className="pt-2 md:pt-[20px] px-2 md:px-4">
				<RecruiterPositionFormHeader
					isFooter={false}
					positionTitle={formatCareerPathType(formData?.careerPath || '')}
					companyDetail={user?.registeredCompany as CompanyDocument}
				/>
			</div>

			<form className="rounded-[12px] px-5 md:px-[30px] py-5 w-full" onSubmit={handleSubmit}>
				<div
					className={classNames(
						'flex items-start gap-4 w-full max-w-[904px]',
						isModal ? 'flex-col md:flex-row' : 'flex-wrap mt-5',
					)}
				>
					<div className="w-full">
						<div className="mb-2 block">
							<Label htmlFor="title" value="Roles hiring" />
							<span className="text-red-600"> *</span>
						</div>
						<Select
							required
							menuPlacement={'auto'}
							menuPosition="fixed"
							options={roleHiringOptions}
							classNamePrefix="flowbite-select"
							value={roleHiringOptions?.find(e => e?.value === formData?.careerPath)}
							onChange={(val: SingleValue<Dropdown>) => {
								setFormData(prev => ({ ...prev, careerPath: val?.value as UserCareerPathType }));
							}}
							placeholder="Frontend Developer"
						/>
					</div>

					<div className="w-full">
						<div className="mb-2 block">
							<Label htmlFor="title" value="Must have skills" />
							<span className="text-[10px] font-[500]"> (min 2 skills required)</span>
							<span className="text-red-600"> *</span>
						</div>
						<Select
							isMulti
							required
							menuPlacement={'auto'}
							menuPosition="fixed"
							options={skillOptions}
							classNamePrefix="flowbite-select"
							value={skillOptions?.filter(title => formData?.techStacks?.includes(title.value))}
							onChange={(val: MultiValue<Dropdown>) => {
								setFormData(prev => ({ ...prev, techStacks: val.map(item => item.value) }));
							}}
							placeholder="React"
						/>

						{formData?.techStacks && formData?.techStacks?.length < 2 ? (
							<div className="mt-2 text-xs text-red-500">Please choose min of 2 skills</div>
						) : null}
					</div>
				</div>

				<div
					className={classNames(
						'flex items-start gap-4 w-full max-w-[904px] mt-5',
						isModal ? 'flex-col md:flex-row' : 'flex-wrap',
					)}
				>
					<div className="w-full">
						<div className="mb-2">
							<Label htmlFor="jobType" value="Job type" />
							<span className="text-[red]"> *</span>
						</div>
						<fieldset
							className="flex flex-wrap max-w-md gap-4"
							onChange={e => {
								const target = e.target as HTMLInputElement;
								if (target?.value) {
									setFormData(prev => ({ ...prev, workingMode: target?.value as WorkingMode }));
								}
							}}
						>
							{WorkingMode &&
								Object.values(WorkingMode).map((mode: string, i: number) => (
									<div key={i} className="flex items-center gap-2">
										<Radio
											className="text-[#7777E7] focus:ring-[#7777E7]"
											id={mode}
											value={mode}
											name="workingMode"
											checked={formData?.workingMode === mode}
											onChange={e => {
												//
											}}
										/>
										<Label htmlFor={mode}>{camelCaseToSpace(mode)}</Label>
									</div>
								))}
						</fieldset>
					</div>

					<div className="w-full">
						<div className="mb-2 block">
							<Label htmlFor="title" value="Location" />
							<span className="text-red-600"> *</span>
						</div>
						<Select
							isMulti
							required
							menuPlacement={'auto'}
							menuPosition="fixed"
							classNamePrefix="flowbite-select"
							options={locationOptionsDataWithAny}
							value={locationOptionsDataWithAny.filter(title => formData?.location?.includes(title.value))}
							onChange={(val: MultiValue<Dropdown>) => {
								// setFormData(prev => ({...prev,location: val?.value || '',}));
								setFormData(prev => ({ ...prev, location: val.map(item => item.value) }));

							}}
							placeholder="Bengaluru"
						/>
					</div>
				</div>

				<div
					className={classNames(
						'flex items-start w-full max-w-[904px] mt-5',
						isModal ? 'flex-col md:flex-row gap-4' : 'flex-wrap gap-4',
					)}
				>
					<div className="w-full [&_.MuiSlider-markLabel]:!text-[10px]">
						<div className="mb-2">
							<Label htmlFor="openings" value="Salary range" />
							<span className="text-[10px] font-[500]"> (per annum INR)</span>
							<span className="text-[red]"> *</span>
						</div>

						<div className="text-[#4B5563] font-[500] text-[16px]">
							{formData?.salary?.min >= 100 ? formData?.salary?.min / 100 + ' CrPA' : formData?.salary?.min + ' LPA'} -{' '}
							{formData?.salary?.max >= 100 ? formData?.salary?.max / 100 + ' CrPA' : formData?.salary?.max + ' LPA'}
						</div>

						<div className="px-2 [&_.MuiSlider-root]:!mb-[0px]">
							<PrettoSlider
								min={1}
								max={80}
								defaultValue={[1, 80]}
								marks={[
									{ value: 1, label: '1 LPA' },
									{ value: 80, label: '80 LPA' },
								]}
								value={[formData?.salary?.min || 1, formData?.salary?.max || 200]}
								onChange={(e: Event, newValue: number | number[]) => {
									if (!Array.isArray(newValue)) {
										return;
									}

									setFormData(prev => ({
										...prev,
										salary: {
											min: newValue[0],
											max: newValue[1],
										},
									}));
								}}
							/>
						</div>
					</div>

					<div className="w-full [&_.MuiSlider-markLabel]:!text-[10px]">
						<div className="mb-2">
							<Label htmlFor="openings" value="Degree" />
							<span className="text-[red]"> *</span>
						</div>

						<Select
							isMulti
							required
							menuPosition="fixed"
							menuPlacement={'auto'}
							classNamePrefix="flowbite-select"
							options={educationOptionsDataWithAny}
							value={educationOptionsDataWithAny.filter(title => formData?.degrees?.includes(title.value))}
							onChange={(val: MultiValue<Dropdown>) => {
								setFormData(prev => ({
									...prev,
									degrees: val?.map(item => item.value) || [],
								}));
							}}
							placeholder="BA"
						/>
					</div>
				</div>

				<div
					className={classNames(
						'grid md:grid-cols-2 items-start w-full max-w-[904px] mt-5',
						isModal ? 'flex-col md:flex-row gap-4' : 'flex-wrap gap-4',
					)}
				>
					<div className="w-full [&_.MuiSlider-markLabel]:!text-[10px]">
						<div className="mb-2">
							<Label htmlFor="openings" value="Availability" />
							<span className="text-[red]"> *</span>
						</div>

						<Select
							required
							menuPosition="fixed"
							menuPlacement={'auto'}
							classNamePrefix="flowbite-select"
							options={availabilityOptionsWithAny}
							value={availabilityOptionsWithAny.find(title => formData?.expectedJoining === title.value)}
							onChange={(val: SingleValue<Dropdown>) => {
								setFormData(prev => ({
									...prev,
									expectedJoining: val?.value || '',
								}));
							}}
							placeholder="Immediate"
						/>
					</div>
				</div>

				<div
					className={classNames(
						'bg-white md:bg-inherit dark:bg-[#111E2C] sticky flex items-center gap-5 bottom-0 mb-[-16px] md:mb-[-20px] mx-[-20px] md:mx-[-30px] px-[16px] md:px-[30px] rounded-b-[12px]',
						!isSticky ? 'border-t md:border-0' : '',
						isModal ? 'mt-2 md:mt-0 py-3' : 'py-5',
					)}
				>
					<Button
						size="md"
						type="submit"
						color="purplePlaced"
						disabled={
							createPositionV2Fn.isLoading || !formData?.techStacks || formData?.techStacks?.length < 2 || loading
						}
						isProcessing={createPositionV2Fn.isLoading || loading}
						className={classNames('font-inter w-full ', isModal ? 'md:max-w-[167px] min-w-fit' : '')}
					>
						Confirm & Submit
					</Button>
				</div>
			</form>
		</div>
	);
};

export default PositionCreationModalFormForRecruiter;
